import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import { formatDateTime } from "../../lib/dateFormat";
import toast from "react-hot-toast";

type Tag = { id: string; name: string; color: string; _count?: { links: number } };
type List = { id: string; name: string; description: string | null; _count?: { members: number } };
type Contact = {
  id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  club_name: string | null;
  club_city: string | null;
  club_county: string | null;
  website: string | null;
  athletes_count: number | null;
  coaches_count: number | null;
  unsubscribed_at: string | null;
  created_at: string;
  tags: { tag: Tag }[];
  lists: { list: { id: string; name: string } }[];
};

/** The editable profile fields, in the order the admin asked to see them. */
const CONTACT_FIELDS: { key: keyof Contact; label: string; type?: "number" }[] = [
  { key: "first_name", label: "Prenume" },
  { key: "last_name", label: "Nume" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefon" },
  { key: "club_name", label: "Nume club" },
  { key: "club_city", label: "Oraș club" },
  { key: "club_county", label: "Județ club" },
  { key: "website", label: "Website" },
  { key: "athletes_count", label: "Nr. sportivi", type: "number" },
  { key: "coaches_count", label: "Nr. antrenori", type: "number" },
];

/**
 * Variables usable in a template or campaign. Mirrors CONTACT_VARIABLES on the
 * server, which is what actually substitutes them at send time.
 */
const TEMPLATE_VARS: { key: string; label: string }[] = [
  { key: "first_name", label: "Prenume" },
  { key: "last_name", label: "Nume" },
  { key: "full_name", label: "Nume complet" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefon" },
  { key: "club_name", label: "Nume club" },
  { key: "club_city", label: "Oraș club" },
  { key: "club_county", label: "Județ club" },
  { key: "website", label: "Website" },
  { key: "athletes_count", label: "Nr. sportivi" },
  { key: "coaches_count", label: "Nr. antrenori" },
];

const VariableHelp = () => (
  <div className="px-3 py-2.5 text-xs border rounded-lg bg-slate-50 border-slate-200 text-slate-600">
    <p className="mb-1.5 font-semibold text-slate-700">Variabile disponibile</p>
    <div className="flex flex-wrap gap-1.5">
      {TEMPLATE_VARS.map((v) => (
        <code
          key={v.key}
          title={v.label}
          className="px-1.5 py-0.5 font-mono bg-white border rounded border-slate-200"
        >{`{{${v.key}}}`}</code>
      ))}
    </div>
    <p className="mt-1.5">
      Merg în subiect și în conținut. Un contact fără valoarea respectivă primește text gol; o
      variabilă scrisă greșit rămâne vizibilă ca atare, ca s-o prinzi în emailul de test.
    </p>
  </div>
);
type Template = { id: string; name: string; subject: string; body_html: string; updated_at: string };
type Campaign = {
  id: string;
  name: string;
  subject: string;
  subject_b: string | null;
  ab_split_pct: number;
  body_html: string;
  list_id: string | null;
  status: "draft" | "sending" | "sent" | "failed";
  total_count: number;
  sent_count: number;
  failed_count: number;
  sent_at: string | null;
  created_at: string;
  list?: { id: string; name: string } | null;
};
type Click = { url: string; click_count: number; last_clicked_at: string };
type Recipient = {
  id: string;
  email: string;
  status: string;
  variant: string | null;
  error: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  clicks: Click[];
};
/** A clicked link, with how many distinct people clicked it. */
type LinkStat = { url: string; clicks: number; people: number };

type TabId = "contacts" | "lists" | "tags" | "templates" | "campaigns" | "settings";

type MarketingSettings = {
  sender_name: string | null;
  sender_email: string | null;
  reply_to: string | null;
  footer_html: string | null;
  company_address: string | null;
  auto_unsubscribe_link: boolean;
};

const TABS: { id: TabId; label: string }[] = [
  { id: "contacts", label: "Contacte" },
  { id: "lists", label: "Liste" },
  { id: "tags", label: "Etichete" },
  { id: "templates", label: "Șabloane" },
  { id: "campaigns", label: "Campanii" },
  { id: "settings", label: "Setări" },
];

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-slate-100 text-slate-600",
  sent: "bg-sky-100 text-sky-700",
  delivered: "bg-emerald-100 text-emerald-700",
  opened: "bg-violet-100 text-violet-700",
  clicked: "bg-indigo-100 text-indigo-700",
  bounced: "bg-rose-100 text-rose-700",
  spam: "bg-orange-100 text-orange-700",
  unsubscribed: "bg-amber-100 text-amber-700",
  failed: "bg-rose-100 text-rose-700",
  draft: "bg-slate-100 text-slate-600",
  sending: "bg-amber-100 text-amber-700",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "În așteptare",
  sent: "Trimis",
  delivered: "Livrat",
  opened: "Deschis",
  clicked: "Click",
  bounced: "Respins",
  spam: "Spam",
  unsubscribed: "Dezabonat",
  failed: "Eșuat",
  draft: "Ciornă",
  sending: "Se trimite",
};

const Pill = ({ status }: { status: string }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

/**
 * Every form control on this page. The text colour is explicit on purpose:
 * :root declares color-scheme: dark, so a control that only sets a white
 * background inherits the browser's DARK default text colour — white on white,
 * i.e. you cannot see what you are typing.
 */
const inputClass =
  "w-full px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";

/**
 * Email marketing for platform admins.
 *
 * One page with tabs rather than five screens: the objects are small and
 * heavily cross-referenced (a contact belongs to lists and carries tags, a
 * campaign targets a list), so splitting them up would mostly add navigation.
 */
export default function Marketing() {
  const [tab, setTab] = useState<TabId>("contacts");

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactTotal, setContactTotal] = useState(0);
  const [lists, setLists] = useState<List[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [settings, setSettings] = useState<MarketingSettings>({
    sender_name: "",
    sender_email: "",
    reply_to: "",
    footer_html: "",
    company_address: "",
    auto_unsubscribe_link: true,
  });
  const [loading, setLoading] = useState(true);

  // Bulk selection over the contacts table.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkListId, setBulkListId] = useState("");

  // Contact filters
  const [search, setSearch] = useState("");
  const [filterList, setFilterList] = useState("");
  const [filterTag, setFilterTag] = useState("");

  // Forms
  const [addMode, setAddMode] = useState<"single" | "bulk" | "csv">("single");
  const [addEmails, setAddEmails] = useState("");
  const [addFields, setAddFields] = useState<Record<string, string>>({});
  const [addLists, setAddLists] = useState<string[]>([]);
  const [addTags, setAddTags] = useState<string[]>([]);
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);

  // Preview of the composed email, straight from the server so it matches the
  // send exactly (variables substituted, footer appended).
  const [preview, setPreview] = useState<{ subject: string; html: string; using_sample: boolean } | null>(null);

  // Per-template test send: which template's address box is open, and what is
  // typed in it. Keyed by id so two templates cannot share one input.
  const [tplTestFor, setTplTestFor] = useState<string | null>(null);
  const [tplTestEmail, setTplTestEmail] = useState("");

  const [listForm, setListForm] = useState({ name: "", description: "" });
  const [tagForm, setTagForm] = useState({ name: "", color: "#6366f1" });

  const [contactDraft, setContactDraft] = useState<Contact | null>(null);
  const [tplDraft, setTplDraft] = useState<Partial<Template> | null>(null);
  const [campDraft, setCampDraft] = useState<Partial<Campaign> | null>(null);

  // Campaign detail (tracking)
  type VariantStats = { sent: number; delivered: number; opened: number; clicked: number; bounced: number; failed: number; total: number };
  const [openCampaign, setOpenCampaign] = useState<{
    campaign: Campaign;
    recipients: Recipient[];
    stats: Record<string, number>;
    ab: { A: VariantStats; B: VariantStats } | null;
    links: LinkStat[];
    recipients_total: number;
  } | null>(null);
  const [testEmail, setTestEmail] = useState("");
  // Which status the recipient list is narrowed to inside the detail view.
  const [recipientFilter, setRecipientFilter] = useState("");

  const loadContacts = useCallback(async () => {
    const params: any = { limit: 100 };
    if (search) params.search = search;
    if (filterList) params.list_id = filterList;
    if (filterTag) params.tag_id = filterTag;
    const { data } = await api.get("/marketing/contacts", { params });
    setContacts(data.contacts || []);
    setContactTotal(data.total || 0);
  }, [search, filterList, filterTag]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, tg, tpl, camp, st] = await Promise.all([
        api.get("/marketing/lists"),
        api.get("/marketing/tags"),
        api.get("/marketing/templates"),
        api.get("/marketing/campaigns"),
        api.get("/marketing/settings"),
      ]);
      setLists(l.data.lists || []);
      setTags(tg.data.tags || []);
      setTemplates(tpl.data.templates || []);
      setCampaigns(camp.data.campaigns || []);
      setSettings({
        sender_name: st.data?.sender_name || "",
        sender_email: st.data?.sender_email || "",
        reply_to: st.data?.reply_to || "",
        footer_html: st.data?.footer_html || "",
        company_address: st.data?.company_address || "",
        auto_unsubscribe_link: st.data?.auto_unsubscribe_link !== false,
      });
      await loadContacts();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut încărca datele.");
    }
    setLoading(false);
  }, [loadContacts]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) loadContacts().catch(() => {});
    // A filter change swaps the rows under the selection, so drop it — acting
    // on contacts you can no longer see is exactly the kind of bulk mistake
    // that is painful to undo.
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterList, filterTag]);

  // ---------------------------------------------------------------- actions --

  const addContacts = async () => {
    const emails = addMode === "single" ? addFields.email || "" : addEmails;
    if (!emails.trim()) return toast.error("Introdu cel puțin o adresă.");
    setBusy(true);
    try {
      const payload: any = { emails, list_ids: addLists, tag_ids: addTags };
      // A single contact carries its whole profile in the same request — no
      // "save, then reopen to edit" round trip.
      if (addMode === "single") {
        for (const f of CONTACT_FIELDS) {
          if (f.key === "email") continue;
          payload[f.key] = addFields[f.key as string] ?? "";
        }
      }
      const { data } = await api.post("/marketing/contacts", payload);
      const parts = [`${data.created} adăugate`];
      if (data.existing) parts.push(`${data.existing} existau deja`);
      if (data.invalid?.length) parts.push(`${data.invalid.length} invalide`);
      toast.success(parts.join(", "));
      setAddEmails("");
      setAddFields({});
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut adăuga contactele.");
    }
    setBusy(false);
  };

  const importCsv = async () => {
    if (!csvText.trim()) return toast.error("Încarcă un fișier sau lipește conținutul CSV.");
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/contacts/import", {
        csv: csvText,
        list_ids: addLists,
        tag_ids: addTags,
      });
      const parts = [`${data.created} adăugate`, `${data.updated} actualizate`];
      if (data.invalid?.length) parts.push(`${data.invalid.length} rânduri invalide`);
      toast.success(parts.join(", "));
      setCsvText("");
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Importul a eșuat.");
    }
    setBusy(false);
  };

  const sendTemplateTest = async (templateId: string) => {
    setBusy(true);
    try {
      await api.post(`/marketing/templates/${templateId}/test`, { email: tplTestEmail });
      toast.success("Email de test trimis.");
      setTplTestFor(null);
      setTplTestEmail("");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Testul nu a putut fi trimis.");
    }
    setBusy(false);
  };

  const openPreview = async (subject: string, bodyHtml: string) => {
    try {
      const { data } = await api.post("/marketing/preview", { subject, body_html: bodyHtml });
      setPreview(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut genera previzualizarea.");
    }
  };

  const setContactLists = async (contactId: string, listIds: string[]) => {
    try {
      await api.post(`/marketing/contacts/${contactId}/lists`, { list_ids: listIds });
      await loadContacts();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut actualiza listele.");
    }
  };

  const setContactTags = async (contactId: string, tagIds: string[]) => {
    try {
      await api.post(`/marketing/contacts/${contactId}/tags`, { tag_ids: tagIds });
      await loadContacts();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut actualiza etichetele.");
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(contacts.map((c) => c.id)));
  };

  const bulkList = async (action: "add" | "remove") => {
    if (selectedIds.size === 0) return toast.error("Selectează cel puțin un contact.");
    if (!bulkListId) return toast.error("Alege lista.");
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/contacts/bulk-lists", {
        contact_ids: Array.from(selectedIds),
        list_id: bulkListId,
        action,
      });
      toast.success(
        action === "add"
          ? `${data.affected} contacte adăugate în „${data.list}”.`
          : `${data.affected} contacte scoase din „${data.list}”.`
      );
      setSelectedIds(new Set());
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Operațiunea a eșuat.");
    }
    setBusy(false);
  };

  const saveContact = async () => {
    if (!contactDraft) return;
    setBusy(true);
    try {
      const payload: any = {};
      for (const f of CONTACT_FIELDS) payload[f.key] = (contactDraft as any)[f.key] ?? "";
      await api.patch(`/marketing/contacts/${contactDraft.id}`, payload);
      setContactDraft(null);
      await loadContacts();
      toast.success("Contact salvat.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut salva contactul.");
    }
    setBusy(false);
  };

  const toggleUnsub = async (c: Contact) => {
    try {
      await api.patch(`/marketing/contacts/${c.id}`, { unsubscribed: !c.unsubscribed_at });
      await loadContacts();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut schimba starea.");
    }
  };

  const deleteContact = async (id: string) => {
    try {
      await api.delete(`/marketing/contacts/${id}`);
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge contactul.");
    }
  };

  const createList = async () => {
    if (!listForm.name.trim()) return toast.error("Numele listei este obligatoriu.");
    try {
      await api.post("/marketing/lists", listForm);
      setListForm({ name: "", description: "" });
      await loadAll();
      toast.success("Listă creată.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut crea lista.");
    }
  };

  const deleteList = async (id: string) => {
    try {
      await api.delete(`/marketing/lists/${id}`);
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge lista.");
    }
  };

  const createTag = async () => {
    if (!tagForm.name.trim()) return toast.error("Numele etichetei este obligatoriu.");
    try {
      await api.post("/marketing/tags", tagForm);
      setTagForm({ name: "", color: "#6366f1" });
      await loadAll();
      toast.success("Etichetă creată.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut crea eticheta.");
    }
  };

  const deleteTag = async (id: string) => {
    try {
      await api.delete(`/marketing/tags/${id}`);
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge eticheta.");
    }
  };

  const saveTemplate = async () => {
    if (!tplDraft?.name || !tplDraft?.subject || !tplDraft?.body_html) {
      return toast.error("Nume, subiect și conținut sunt obligatorii.");
    }
    setBusy(true);
    try {
      if (tplDraft.id) await api.put(`/marketing/templates/${tplDraft.id}`, tplDraft);
      else await api.post("/marketing/templates", tplDraft);
      setTplDraft(null);
      await loadAll();
      toast.success("Șablon salvat.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut salva șablonul.");
    }
    setBusy(false);
  };

  const saveCampaign = async () => {
    if (!campDraft?.name || !campDraft?.subject || !campDraft?.body_html) {
      return toast.error("Nume, subiect și conținut sunt obligatorii.");
    }
    setBusy(true);
    try {
      if (campDraft.id) await api.put(`/marketing/campaigns/${campDraft.id}`, campDraft);
      else await api.post("/marketing/campaigns", campDraft);
      setCampDraft(null);
      await loadAll();
      toast.success("Campanie salvată.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut salva campania.");
    }
    setBusy(false);
  };

  const openTracking = async (id: string, status = "") => {
    try {
      const { data } = await api.get(`/marketing/campaigns/${id}`, {
        params: status ? { status } : {},
      });
      setOpenCampaign(data);
      setRecipientFilter(status);
      if (!status) setTestEmail("");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut încărca campania.");
    }
  };

  const sendTest = async () => {
    if (!openCampaign) return;
    setBusy(true);
    try {
      await api.post(`/marketing/campaigns/${openCampaign.campaign.id}/test`, { email: testEmail });
      toast.success("Email de test trimis.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Testul nu a putut fi trimis.");
    }
    setBusy(false);
  };

  const sendCampaign = async () => {
    if (!openCampaign) return;
    const list = lists.find((l) => l.id === openCampaign.campaign.list_id);
    const count = list?._count?.members ?? 0;
    if (
      !window.confirm(
        `Trimiți campania "${openCampaign.campaign.name}" către lista "${list?.name || "—"}" (${count} contacte)? Acțiunea nu poate fi anulată.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/campaigns/${openCampaign.campaign.id}/send`);
      toast.success(`Trimise: ${data.sent}, eșuate: ${data.failed}`);
      await loadAll();
      await openTracking(openCampaign.campaign.id, recipientFilter);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Trimiterea a eșuat.");
    }
    setBusy(false);
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/marketing/settings", settings);
      setSettings({
        sender_name: data?.sender_name || "",
        sender_email: data?.sender_email || "",
        reply_to: data?.reply_to || "",
        footer_html: data?.footer_html || "",
        company_address: data?.company_address || "",
        auto_unsubscribe_link: data?.auto_unsubscribe_link !== false,
      });
      toast.success("Setări salvate.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut salva setările.");
    }
    setBusy(false);
  };

  const deleteCampaign = async (id: string) => {
    try {
      await api.delete(`/marketing/campaigns/${id}`);
      await loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge campania.");
    }
  };

  // ------------------------------------------------------------------- view --

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Marketing email"
        subtitle="Liste de contacte, etichete, șabloane și campanii trimise prin Brevo."
      />

      <div className="flex flex-wrap gap-2">
        {TABS.map((tItem) => (
          <button
            key={tItem.id}
            type="button"
            onClick={() => setTab(tItem.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-xl border transition ${
              tab === tItem.id
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {tItem.label}
          </button>
        ))}
      </div>

      {loading ? (
        <SectionCard title="" bodyPadded>
          <p className="text-sm text-slate-500">Se încarcă...</p>
        </SectionCard>
      ) : (
        <>
          {/* ---------------------------------------------------- CONTACTS -- */}
          {tab === "contacts" && (
            <>
              <SectionCard title="Adaugă contacte" bodyPadded>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {([
                      ["single", "Un contact"],
                      ["bulk", "Mai multe adrese"],
                      ["csv", "Import CSV"],
                    ] as [typeof addMode, string][]).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setAddMode(mode)}
                        className={`px-3 py-1.5 text-sm font-semibold rounded-lg border transition ${
                          addMode === mode
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "bg-white text-slate-600 border-slate-200"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* One contact, with its whole profile in the same form. */}
                  {addMode === "single" && (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {CONTACT_FIELDS.map((f) => (
                        <div key={String(f.key)}>
                          <label className="block mb-1 text-sm font-medium text-slate-700">
                            {f.label}
                            {f.key === "email" && <span className="text-rose-500"> *</span>}
                          </label>
                          <input
                            type={f.type === "number" ? "number" : "text"}
                            min={f.type === "number" ? 0 : undefined}
                            value={addFields[f.key as string] ?? ""}
                            onChange={(e) =>
                              setAddFields({ ...addFields, [f.key as string]: e.target.value })
                            }
                            className={inputClass}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {addMode === "bulk" && (
                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-700">Adrese de email</label>
                      <textarea
                        rows={4}
                        value={addEmails}
                        onChange={(e) => setAddEmails(e.target.value)}
                        placeholder="una pe linie, sau separate prin virgulă"
                        className={inputClass}
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        Doar adresele. Pentru restul detaliilor folosește Import CSV sau adaugă-le pe rând.
                      </p>
                    </div>
                  )}

                  {addMode === "csv" && (
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => setCsvText(String(reader.result || ""));
                          reader.readAsText(file);
                          e.target.value = "";
                        }}
                        className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:font-semibold"
                      />
                      <textarea
                        rows={5}
                        value={csvText}
                        onChange={(e) => setCsvText(e.target.value)}
                        placeholder="Email;Prenume;Nume;Telefon;Nume club;Oraș club;Județ club;Website;Nr. sportivi;Nr. antrenori"
                        className={`${inputClass} font-mono text-xs`}
                      />
                      <div className="px-3 py-2.5 text-xs border rounded-lg bg-slate-50 border-slate-200 text-slate-600">
                        <p className="mb-1 font-semibold text-slate-700">Cum trebuie să arate fișierul</p>
                        <p>
                          Primul rând este antetul. Coloanele se recunosc după nume, în română sau engleză, cu
                          sau fără diacritice — ordinea nu contează, iar coloanele necunoscute sunt ignorate:
                          <code className="px-1 mx-1 font-mono bg-white border rounded border-slate-200">
                            email, prenume, nume, telefon, nume club, oraș club, județ club, website, nr sportivi, nr antrenori
                          </code>
                        </p>
                        <p className="mt-1">
                          Separatorul (<code>,</code> <code>;</code> tab) e detectat automat. Un contact existent
                          este actualizat, nu duplicat, iar o celulă goală nu șterge o valoare deja salvată.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-700">Adaugă în listele</label>
                      <select
                        multiple
                        value={addLists}
                        onChange={(e) => setAddLists(Array.from(e.target.selectedOptions, (o) => o.value))}
                        className={`${inputClass} h-24`}
                      >
                        {lists.map((l) => (
                          <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-slate-700">Etichete</label>
                      <select
                        multiple
                        value={addTags}
                        onChange={(e) => setAddTags(Array.from(e.target.selectedOptions, (o) => o.value))}
                        className={`${inputClass} h-24`}
                      >
                        {tags.map((tg) => (
                          <option key={tg.id} value={tg.id}>{tg.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <AdminButton onClick={addMode === "csv" ? importCsv : addContacts} disabled={busy}>
                    {busy ? "Se procesează..." : addMode === "csv" ? "Importă" : "Adaugă"}
                  </AdminButton>
                </div>
              </SectionCard>

              <SectionCard title={`Contacte (${contactTotal})`} bodyPadded>
                <div className="grid gap-3 mb-4 sm:grid-cols-3">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Caută după email sau nume"
                    className={inputClass}
                  />
                  <select value={filterList} onChange={(e) => setFilterList(e.target.value)} className={inputClass}>
                    <option value="">Toate listele</option>
                    {lists.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                  <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className={inputClass}>
                    <option value="">Toate etichetele</option>
                    {tags.map((tg) => (
                      <option key={tg.id} value={tg.id}>{tg.name}</option>
                    ))}
                  </select>
                </div>

                {/* Bulk bar. Only appears with a selection, so it never competes
                    with the filters for attention. */}
                {selectedIds.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 p-3 mb-4 border rounded-xl border-indigo-200 bg-indigo-50">
                    <span className="text-sm font-bold text-indigo-900">
                      {selectedIds.size} selectate
                    </span>
                    <select
                      value={bulkListId}
                      onChange={(e) => setBulkListId(e.target.value)}
                      className={`${inputClass} w-auto min-w-[200px]`}
                    >
                      <option value="">Alege lista</option>
                      {lists.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({l._count?.members ?? 0})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => bulkList("add")}
                      disabled={busy || !bulkListId}
                      className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-50"
                    >
                      Adaugă în listă
                    </button>
                    <button
                      type="button"
                      onClick={() => bulkList("remove")}
                      disabled={busy || !bulkListId}
                      className="px-4 py-2 text-sm font-semibold bg-white border rounded-lg border-slate-300 text-slate-700 disabled:opacity-50"
                    >
                      Scoate din listă
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedIds(new Set())}
                      className="px-2 py-2 ml-auto text-sm font-semibold text-slate-500 hover:text-slate-700"
                    >
                      Anulează selecția
                    </button>
                  </div>
                )}

                {contacts.length === 0 ? (
                  <p className="text-sm text-slate-500">Niciun contact.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                          <th className="py-2 pr-3 w-8">
                            <input
                              type="checkbox"
                              checked={allVisibleSelected}
                              onChange={toggleSelectAll}
                              title="Selectează tot ce se vede"
                              className="w-4 h-4 rounded border-slate-300"
                            />
                          </th>
                          <th className="py-2 pr-4 text-left">Nume</th>
                          <th className="py-2 pr-4 text-left">Email</th>
                          <th className="py-2 pr-4 text-left">Club</th>
                          <th className="py-2 pr-4 text-left">Liste</th>
                          <th className="py-2 text-right">Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* The row is the way in: clicking it opens everything
                            about the contact. The table itself stays narrow
                            enough to read without scrolling sideways. */}
                        {contacts.map((c) => (
                          <tr
                            key={c.id}
                            onClick={() => setContactDraft(c)}
                            className={`border-t cursor-pointer border-slate-100 hover:bg-slate-50 ${
                              selectedIds.has(c.id) ? "bg-indigo-50/60" : ""
                            }`}
                          >
                            <td className="py-3 pr-3" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(c.id)}
                                onChange={() => toggleSelected(c.id)}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                            </td>
                            <td className="py-3 pr-4 text-slate-800 whitespace-nowrap">
                              {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—"}
                            </td>
                            <td className="py-3 pr-4 whitespace-nowrap">
                              <span className={c.unsubscribed_at ? "line-through text-slate-400" : "text-slate-700"}>
                                {c.email}
                              </span>
                              {c.unsubscribed_at && <span className="ml-2"><Pill status="unsubscribed" /></span>}
                            </td>
                            <td className="py-3 pr-4 text-slate-600">
                              {c.club_name || "—"}
                              {c.club_city && <span className="text-slate-400"> · {c.club_city}</span>}
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex flex-wrap gap-1">
                                {c.lists.length === 0 && <span className="text-slate-400">—</span>}
                                {c.lists.map((x) => (
                                  <span
                                    key={x.list.id}
                                    className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600"
                                  >
                                    {x.list.name}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                              {/* stopPropagation, or the row click would reopen
                                  the modal on top of the action. */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleUnsub(c); }}
                                className="px-2 py-1 text-xs font-semibold text-amber-600 hover:text-amber-700"
                              >
                                {c.unsubscribed_at ? "Reabonează" : "Dezabonează"}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); deleteContact(c.id); }}
                                className="px-2 py-1 text-xs font-semibold text-slate-400 hover:text-rose-600"
                              >
                                Șterge
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* ------------------------------------------------------- LISTS -- */}
          {tab === "lists" && (
            <SectionCard title="Liste de contacte" bodyPadded>
              <div className="grid gap-3 mb-5 sm:grid-cols-[1fr_2fr_auto]">
                <input
                  value={listForm.name}
                  onChange={(e) => setListForm({ ...listForm, name: e.target.value })}
                  placeholder="Nume listă"
                  className={inputClass}
                />
                <input
                  value={listForm.description}
                  onChange={(e) => setListForm({ ...listForm, description: e.target.value })}
                  placeholder="Descriere (opțional)"
                  className={inputClass}
                />
                <AdminButton onClick={createList}>Adaugă</AdminButton>
              </div>

              {lists.length === 0 ? (
                <p className="text-sm text-slate-500">Nicio listă.</p>
              ) : (
                <div className="space-y-2">
                  {lists.map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center gap-3 px-4 py-3 border rounded-xl border-slate-200"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800">{l.name}</p>
                        {l.description && <p className="text-xs text-slate-500">{l.description}</p>}
                      </div>
                      <span className="text-sm font-bold text-slate-500 tabular-nums">
                        {l._count?.members ?? 0} contacte
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteList(l.id)}
                        className="text-xs font-semibold text-slate-400 hover:text-rose-600"
                      >
                        Șterge
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* -------------------------------------------------------- TAGS -- */}
          {tab === "tags" && (
            <SectionCard title="Etichete" bodyPadded>
              <div className="flex flex-wrap items-end gap-3 mb-5">
                <div className="flex-1 min-w-[200px]">
                  <label className="block mb-1 text-sm font-medium text-slate-700">Nume</label>
                  <input
                    value={tagForm.name}
                    onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-700">Culoare</label>
                  <input
                    type="color"
                    value={tagForm.color}
                    onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
                    className="w-16 h-10 border rounded-lg border-slate-300"
                  />
                </div>
                <AdminButton onClick={createTag}>Adaugă</AdminButton>
              </div>

              {tags.length === 0 ? (
                <p className="text-sm text-slate-500">Nicio etichetă.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tg) => (
                    <span
                      key={tg.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold text-white"
                      style={{ backgroundColor: tg.color }}
                    >
                      {tg.name}
                      <span className="text-xs opacity-80">{tg._count?.links ?? 0}</span>
                      <button type="button" onClick={() => deleteTag(tg.id)} className="opacity-70 hover:opacity-100">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* --------------------------------------------------- TEMPLATES -- */}
          {tab === "templates" && (
            <SectionCard
              title="Șabloane"
              bodyPadded
              actions={
                <AdminButton onClick={() => setTplDraft({ name: "", subject: "", body_html: "" })}>
                  Șablon nou
                </AdminButton>
              }
            >
              {templates.length === 0 ? (
                <p className="text-sm text-slate-500">Niciun șablon.</p>
              ) : (
                <div className="space-y-2">
                  {templates.map((tpl) => (
                    <div key={tpl.id} className="px-4 py-3 border rounded-xl border-slate-200">
                      <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800">{tpl.name}</p>
                        <p className="text-xs truncate text-slate-500">{tpl.subject}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openPreview(tpl.subject, tpl.body_html)}
                        className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                      >
                        Previzualizează
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setTplTestFor(tplTestFor === tpl.id ? null : tpl.id);
                          setTplTestEmail("");
                        }}
                        className="text-xs font-semibold text-sky-600 hover:text-sky-700"
                      >
                        Trimite test
                      </button>
                      <button
                        type="button"
                        onClick={() => setTplDraft(tpl)}
                        className="text-xs font-semibold text-indigo-600"
                      >
                        Editează
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setCampDraft({
                            name: tpl.name,
                            subject: tpl.subject,
                            body_html: tpl.body_html,
                            list_id: null,
                          })
                        }
                        className="text-xs font-semibold text-emerald-600"
                      >
                        Fă o campanie
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          await api.delete(`/marketing/templates/${tpl.id}`);
                          await loadAll();
                        }}
                        className="text-xs font-semibold text-slate-400 hover:text-rose-600"
                      >
                        Șterge
                      </button>
                      </div>

                      {tplTestFor === tpl.id && (
                        <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-slate-100">
                          <input
                            value={tplTestEmail}
                            onChange={(e) => setTplTestEmail(e.target.value)}
                            placeholder="adresa pentru test"
                            className={`${inputClass} flex-1 min-w-[200px]`}
                          />
                          <button
                            type="button"
                            onClick={() => sendTemplateTest(tpl.id)}
                            disabled={busy || !tplTestEmail}
                            className="px-4 py-2 text-sm font-bold text-white rounded-lg bg-sky-600 disabled:opacity-60"
                          >
                            {busy ? "Se trimite..." : "Trimite"}
                          </button>
                          <p className="w-full text-xs text-slate-500">
                            Variabilele sunt înlocuite cu valori de exemplu, iar linkul de dezabonare este
                            inactiv. Se folosește expeditorul din Setări.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* --------------------------------------------------- CAMPAIGNS -- */}
          {tab === "campaigns" && (
            <SectionCard
              title="Campanii"
              bodyPadded
              actions={
                <AdminButton onClick={() => setCampDraft({ name: "", subject: "", body_html: "", list_id: null })}>
                  Campanie nouă
                </AdminButton>
              }
            >
              {campaigns.length === 0 ? (
                <p className="text-sm text-slate-500">Nicio campanie.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                        <th className="py-2 pr-4 text-left">Campanie</th>
                        <th className="py-2 pr-4 text-left">Listă</th>
                        <th className="py-2 pr-4 text-left">Stare</th>
                        <th className="py-2 pr-4 text-left">Trimise</th>
                        <th className="py-2 pr-4 text-left">Trimisă la</th>
                        <th className="py-2 text-right">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100">
                          <td className="py-3 pr-4">
                            <p className="font-semibold text-slate-800">{c.name}</p>
                            <p className="text-xs truncate text-slate-500">{c.subject}</p>
                          </td>
                          <td className="py-3 pr-4 text-slate-600">{c.list?.name || "—"}</td>
                          <td className="py-3 pr-4"><Pill status={c.status} /></td>
                          <td className="py-3 pr-4 text-slate-600 tabular-nums">
                            {c.sent_count}/{c.total_count}
                            {c.failed_count > 0 && <span className="ml-1 text-rose-600">({c.failed_count} eșuate)</span>}
                          </td>
                          <td className="py-3 pr-4 text-slate-500">{c.sent_at ? formatDateTime(c.sent_at) : "—"}</td>
                          <td className="py-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openTracking(c.id)}
                              className="px-2 py-1 text-xs font-semibold text-indigo-600"
                            >
                              Detalii
                            </button>
                            {c.status === "draft" && (
                              <button
                                type="button"
                                onClick={() => setCampDraft(c)}
                                className="px-2 py-1 text-xs font-semibold text-slate-500"
                              >
                                Editează
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => deleteCampaign(c.id)}
                              className="px-2 py-1 text-xs font-semibold text-slate-400 hover:text-rose-600"
                            >
                              Șterge
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          )}
          {/* ---------------------------------------------------- SETTINGS -- */}
          {tab === "settings" && (
            <SectionCard
              title="Expeditor și footer"
              subtitle="Se aplică numai emailurilor promoționale. Emailurile tranzacționale folosesc expeditorul din Setări platformă."
              bodyPadded
            >
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Nume expeditor</label>
                    <input
                      value={settings.sender_name || ""}
                      onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                      placeholder="iGROWth"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Adresă expeditor</label>
                    <input
                      value={settings.sender_email || ""}
                      onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                      placeholder="noutati@domeniul-tau.ro"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Adresă de răspuns (opțional)</label>
                    <input
                      value={settings.reply_to || ""}
                      onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })}
                      placeholder="contact@domeniul-tau.ro"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Adresă poștală</label>
                    <input
                      value={settings.company_address || ""}
                      onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                      placeholder="Str. Exemplu 1, București, România"
                      className={inputClass}
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      Apare în footer. Legislația anti-spam o cere în emailurile comerciale.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-700">Footer (HTML)</label>
                  <textarea
                    rows={8}
                    value={settings.footer_html || ""}
                    onChange={(e) => setSettings({ ...settings, footer_html: e.target.value })}
                    placeholder="<p>iGROWth — platforma cluburilor sportive</p>"
                    className={`${inputClass} font-mono text-xs`}
                  />
                </div>

                <div className="px-4 py-3 space-y-2 text-sm border rounded-xl bg-slate-50 border-slate-200 text-slate-600">
                  <label className="flex items-start gap-2 font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={settings.auto_unsubscribe_link}
                      onChange={(e) =>
                        setSettings({ ...settings, auto_unsubscribe_link: e.target.checked })
                      }
                      className="w-4 h-4 mt-0.5 rounded border-slate-300"
                    />
                    Adaugă automat un link de dezabonare la finalul emailului
                  </label>
                  <p>
                    Dacă scrii <code className="px-1 font-mono bg-white border rounded border-slate-200">{`{{unsubscribe_url}}`}</code>{" "}
                    în footerul tău, nu se mai adaugă nimic automat — linkul rămâne acolo unde l-ai pus.
                    La emailul de test linkul este inactiv, deci un test nu poate dezabona pe nimeni.
                  </p>
                  {!settings.auto_unsubscribe_link &&
                    !(settings.footer_html || "").includes("{{unsubscribe_url}}") && (
                      <p className="font-semibold text-amber-700">
                        Atenție: fără link automat și fără {`{{unsubscribe_url}}`} în footer, emailurile pleacă
                        fără nicio cale de dezabonare. Legislația anti-spam o cere, iar destinatarii vor folosi
                        butonul de „spam" în loc.
                      </p>
                    )}
                </div>

                <div className="px-4 py-3 text-sm border rounded-xl bg-emerald-50 border-emerald-200 text-emerald-800">
                  <p className="mb-1 font-semibold">Dezabonarea este doar pentru promoționale</p>
                  <p>
                    Un contact dezabonat este sărit la orice campanie, dar continuă să primească emailurile
                    tranzacționale — invitații, facturi, activare cont, resetare parolă. Dacă devine mai
                    târziu client, nimic din ce ține de contul lui nu este blocat.
                  </p>
                </div>

                <AdminButton onClick={saveSettings} disabled={busy}>
                  {busy ? "Se salvează..." : "Salvează setările"}
                </AdminButton>
              </div>
            </SectionCard>
          )}
        </>
      )}

      {/* -------------------------------------------------- contact editor -- */}
      {contactDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl p-5 space-y-4 bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-800">
                  {[contactDraft.first_name, contactDraft.last_name].filter(Boolean).join(" ") ||
                    contactDraft.name ||
                    contactDraft.email}
                </h2>
                <p className="text-xs text-slate-500">
                  Adăugat pe {formatDateTime(contactDraft.created_at)}
                  {contactDraft.unsubscribed_at && " · dezabonat de la promoționale"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setContactDraft(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {CONTACT_FIELDS.map((f) => (
                <div key={String(f.key)}>
                  <label className="block mb-1 text-sm font-medium text-slate-700">{f.label}</label>
                  <input
                    type={f.type === "number" ? "number" : "text"}
                    min={f.type === "number" ? 0 : undefined}
                    value={(contactDraft as any)[f.key] ?? ""}
                    onChange={(e) =>
                      setContactDraft({ ...contactDraft, [f.key]: e.target.value } as Contact)
                    }
                    className={inputClass}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              Aceste câmpuri alimentează variabilele din șabloane ({`{{first_name}}`}, {`{{club_name}}`} etc.).
            </p>

            {/* Membership lives here now that the table only shows a summary.
                Both save immediately — they are relations, not form fields. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Liste</label>
                <select
                  multiple
                  value={contactDraft.lists.map((x) => x.list.id)}
                  onChange={(e) => {
                    const ids = Array.from(e.target.selectedOptions, (o) => o.value);
                    setContactDraft({
                      ...contactDraft,
                      lists: lists.filter((l) => ids.includes(l.id)).map((l) => ({ list: { id: l.id, name: l.name } })),
                    });
                    setContactLists(contactDraft.id, ids);
                  }}
                  className={`${inputClass} h-28`}
                >
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Etichete</label>
                <select
                  multiple
                  value={contactDraft.tags.map((x) => x.tag.id)}
                  onChange={(e) => {
                    const ids = Array.from(e.target.selectedOptions, (o) => o.value);
                    setContactDraft({
                      ...contactDraft,
                      tags: tags.filter((t) => ids.includes(t.id)).map((t) => ({ tag: t })),
                    });
                    setContactTags(contactDraft.id, ids);
                  }}
                  className={`${inputClass} h-28`}
                >
                  {tags.map((tg) => (
                    <option key={tg.id} value={tg.id}>{tg.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setContactDraft(null)}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={saveContact}
                disabled={busy}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
              >
                {busy ? "Se salvează..." : "Salvează"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------- template editor -- */}
      {tplDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl p-5 space-y-3 bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-800">
              {tplDraft.id ? "Editează șablonul" : "Șablon nou"}
            </h2>
            <input
              value={tplDraft.name || ""}
              onChange={(e) => setTplDraft({ ...tplDraft, name: e.target.value })}
              placeholder="Nume șablon"
              className={inputClass}
            />
            <input
              value={tplDraft.subject || ""}
              onChange={(e) => setTplDraft({ ...tplDraft, subject: e.target.value })}
              placeholder="Subiect"
              className={inputClass}
            />
            <VariableHelp />
            <textarea
              rows={14}
              value={tplDraft.body_html || ""}
              onChange={(e) => setTplDraft({ ...tplDraft, body_html: e.target.value })}
              placeholder="<p>Conținut HTML</p>"
              className={`${inputClass} font-mono text-xs`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTplDraft(null)}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={() => openPreview(tplDraft.subject || "", tplDraft.body_html || "")}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-indigo-200 text-indigo-700 bg-indigo-50"
              >
                Previzualizează
              </button>
              <button
                type="button"
                onClick={saveTemplate}
                disabled={busy}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
              >
                Salvează
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------- campaign editor -- */}
      {campDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl p-5 space-y-3 bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-800">
              {campDraft.id ? "Editează campania" : "Campanie nouă"}
            </h2>
            <input
              value={campDraft.name || ""}
              onChange={(e) => setCampDraft({ ...campDraft, name: e.target.value })}
              placeholder="Nume campanie (intern)"
              className={inputClass}
            />
            <input
              value={campDraft.subject || ""}
              onChange={(e) => setCampDraft({ ...campDraft, subject: e.target.value })}
              placeholder="Subiectul emailului"
              className={inputClass}
            />
            <div className="p-3 space-y-2 border rounded-xl border-slate-200 bg-slate-50">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={campDraft.subject_b !== null && campDraft.subject_b !== undefined}
                  onChange={(e) =>
                    setCampDraft({ ...campDraft, subject_b: e.target.checked ? "" : null })
                  }
                  className="w-4 h-4 rounded border-slate-300"
                />
                Testează două linii de subiect (A/B)
              </label>
              {campDraft.subject_b !== null && campDraft.subject_b !== undefined && (
                <>
                  <input
                    value={campDraft.subject_b || ""}
                    onChange={(e) => setCampDraft({ ...campDraft, subject_b: e.target.value })}
                    placeholder="Subiect varianta B"
                    className={inputClass}
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-600">Procent care primește varianta A</label>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={campDraft.ab_split_pct ?? 50}
                      onChange={(e) =>
                        setCampDraft({ ...campDraft, ab_split_pct: Number(e.target.value) })
                      }
                      className="w-20 px-2 py-1 text-sm bg-white border rounded-lg border-slate-300 text-slate-800"
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Conținutul este identic; diferă doar subiectul. Împărțirea se face o singură dată,
                    amestecând lista, ca proporția să iasă exact — nu prin tragere la sorți per contact.
                  </p>
                </>
              )}
            </div>
            <select
              value={campDraft.list_id || ""}
              onChange={(e) => setCampDraft({ ...campDraft, list_id: e.target.value || null })}
              className={inputClass}
            >
              <option value="">Alege lista destinatară</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l._count?.members ?? 0})
                </option>
              ))}
            </select>
            {templates.length > 0 && !campDraft.id && (
              <select
                onChange={(e) => {
                  const tpl = templates.find((x) => x.id === e.target.value);
                  if (tpl) setCampDraft({ ...campDraft, subject: tpl.subject, body_html: tpl.body_html });
                }}
                className={inputClass}
                defaultValue=""
              >
                <option value="">Pornește de la un șablon…</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
            )}
            <VariableHelp />
            <textarea
              rows={14}
              value={campDraft.body_html || ""}
              onChange={(e) => setCampDraft({ ...campDraft, body_html: e.target.value })}
              placeholder="<p>Conținut HTML</p>"
              className={`${inputClass} font-mono text-xs`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCampDraft(null)}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={() => openPreview(campDraft.subject || "", campDraft.body_html || "")}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-indigo-200 text-indigo-700 bg-indigo-50"
              >
                Previzualizează
              </button>
              <button
                type="button"
                onClick={saveCampaign}
                disabled={busy}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
              >
                Salvează ciorna
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----------------------------------------------- campaign tracking -- */}
      {openCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-3xl p-5 space-y-4 bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-slate-800">{openCampaign.campaign.name}</h2>
                <p className="text-sm truncate text-slate-500">{openCampaign.campaign.subject}</p>
              </div>
              <button type="button" onClick={() => setOpenCampaign(null)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>

            {/* Roll-up straight from the recipient rows, so it always matches
                what the table below shows. */}
            {/* Each figure is also the filter for the list below it — the
                question is always "who", not just "how many". */}
            <div className="flex flex-wrap gap-2">
              {recipientFilter && (
                <button
                  type="button"
                  onClick={() => openTracking(openCampaign.campaign.id, "")}
                  className="px-3 py-1.5 rounded-xl text-sm font-bold bg-indigo-600 text-white"
                >
                  ✕ Toți
                </button>
              )}
              {["sent", "delivered", "opened", "clicked", "bounced", "spam", "unsubscribed", "failed", "pending"]
                .filter((k) => openCampaign.stats[k])
                .map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => openTracking(openCampaign.campaign.id, recipientFilter === k ? "" : k)}
                    className={`px-3 py-1.5 rounded-xl text-sm font-bold transition ${STATUS_STYLE[k]} ${
                      recipientFilter === k ? "ring-2 ring-offset-1 ring-indigo-500" : ""
                    }`}
                  >
                    {STATUS_LABEL[k]}: {openCampaign.stats[k]}
                  </button>
                ))}
              {Object.keys(openCampaign.stats).length === 0 && (
                <span className="text-sm text-slate-500">Campania nu a fost trimisă încă.</span>
              )}
            </div>

            {/* "Livrat"/"Deschis"/"Click" can only ever come from Brevo calling
                us back. Saying so beats letting the columns look broken. */}
            {(openCampaign.stats.sent || 0) > 0 &&
              !openCampaign.stats.delivered &&
              !openCampaign.stats.opened &&
              !openCampaign.stats.clicked && (
                <div className="px-4 py-3 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">
                  <p className="font-semibold">Nu am primit încă niciun raport de livrare</p>
                  <p className="mt-1">
                    „Trimis" înseamnă că Brevo a acceptat emailul. „Livrat", „Deschis" și „Click" vin
                    ulterior, prin webhook. Dacă rămân goale și după câteva minute, verifică în Brevo
                    (Transactional → Settings → Webhook) că există un webhook către{" "}
                    <code className="px-1 font-mono bg-white border rounded border-amber-200">
                      /webhooks/brevo
                    </code>{" "}
                    cu evenimentele delivered, opened, click, bounce și unsubscribed bifate.
                  </p>
                </div>
              )}

            {/* Which links were actually clicked. "Persoane" counts distinct
                recipients — total clicks alone would let one enthusiastic
                reader look like a trend. */}
            {openCampaign.links.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-extrabold tracking-wide uppercase text-slate-400">
                  Linkuri accesate
                </h3>
                <div className="space-y-1.5">
                  {openCampaign.links.map((l) => (
                    <div
                      key={l.url}
                      className="flex items-center gap-3 px-3 py-2 border rounded-lg border-slate-200"
                    >
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="flex-1 min-w-0 text-sm text-indigo-600 truncate hover:underline"
                        title={l.url}
                      >
                        {l.url}
                      </a>
                      <span className="text-xs font-bold text-slate-600 tabular-nums shrink-0">
                        {l.people} {l.people === 1 ? "persoană" : "persoane"}
                      </span>
                      <span className="text-xs text-slate-400 tabular-nums shrink-0">
                        {l.clicks} click{l.clicks === 1 ? "" : "-uri"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {openCampaign.campaign.status === "draft" && (
              <div className="p-4 space-y-3 border rounded-xl border-slate-200 bg-slate-50">
                <div className="flex flex-wrap gap-2">
                  <input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="adresa pentru test"
                    className={`${inputClass} flex-1 min-w-[200px]`}
                  />
                  <button
                    type="button"
                    onClick={sendTest}
                    disabled={busy || !testEmail}
                    className="px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-700 disabled:opacity-60"
                  >
                    Trimite test
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  În test, variabilele sunt înlocuite cu valori de exemplu, iar linkul de dezabonare
                  este inactiv.
                </p>
                <button
                  type="button"
                  onClick={sendCampaign}
                  disabled={busy || !openCampaign.campaign.list_id}
                  className="w-full py-3 text-sm font-bold text-white bg-emerald-600 rounded-xl disabled:opacity-40"
                >
                  {busy ? "Se trimite..." : "Trimite campania"}
                </button>
                {!openCampaign.campaign.list_id && (
                  <p className="text-xs text-amber-700">Alege întâi lista destinatară din editarea campaniei.</p>
                )}
              </div>
            )}

            {openCampaign.recipients.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-extrabold tracking-wide uppercase text-slate-400">
                  Destinatari
                  {recipientFilter && ` · ${STATUS_LABEL[recipientFilter] || recipientFilter}`}
                  <span className="ml-2 font-semibold normal-case text-slate-400">
                    {openCampaign.recipients.length} din {openCampaign.recipients_total}
                  </span>
                </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                      <th className="py-2 pr-4 text-left">Destinatar</th>
                      <th className="py-2 pr-4 text-left">Stare</th>
                      <th className="py-2 pr-4 text-left">Trimis</th>
                      <th className="py-2 pr-4 text-left">Livrat</th>
                      <th className="py-2 pr-4 text-left">Deschis</th>
                      <th className="py-2 text-left">Linkuri accesate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openCampaign.recipients.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-2 pr-4 text-slate-700">
                          {r.email}
                          {r.error && <p className="text-xs text-rose-600">{r.error}</p>}
                        </td>
                        <td className="py-2 pr-4"><Pill status={r.status} /></td>
                        <td className="py-2 pr-4 text-xs text-slate-500">
                          {r.sent_at ? formatDateTime(r.sent_at) : "—"}
                        </td>
                        <td className="py-2 pr-4 text-xs text-slate-500">
                          {r.delivered_at ? formatDateTime(r.delivered_at) : "—"}
                        </td>
                        <td className="py-2 pr-4 text-xs text-slate-500">
                          {r.opened_at ? formatDateTime(r.opened_at) : "—"}
                        </td>
                        <td className="py-2 text-xs">
                          {r.clicks.length === 0 ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <div className="space-y-0.5 max-w-[260px]">
                              {r.clicks.map((cl) => (
                                <a
                                  key={cl.url}
                                  href={cl.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  title={`${cl.url} · ${cl.click_count} click-uri`}
                                  className="block text-indigo-600 truncate hover:underline"
                                >
                                  {cl.url}
                                  {cl.click_count > 1 && (
                                    <span className="text-slate-400"> ×{cl.click_count}</span>
                                  )}
                                </a>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {openCampaign.recipients_total > openCampaign.recipients.length && (
                  <p className="mt-2 text-xs text-slate-400">
                    Se afișează primele {openCampaign.recipients.length} rânduri. Filtrează după stare
                    ca să vezi restul.
                  </p>
                )}
              </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ------------------------------------------------------- preview -- */}
      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="w-full max-w-3xl bg-white rounded-2xl flex flex-col max-h-[92vh]">
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
              <div className="min-w-0">
                <p className="text-xs font-semibold tracking-wide uppercase text-slate-400">Subiect</p>
                <p className="font-bold text-slate-800">{preview.subject || "(fără subiect)"}</p>
                {preview.using_sample && (
                  <p className="mt-1 text-xs text-slate-500">
                    Variabilele sunt înlocuite cu valori de exemplu.
                  </p>
                )}
              </div>
              <button type="button" onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            {/* An iframe, so the email's own CSS cannot leak into the admin
                page — and so it renders in its own document, the way a mail
                client shows it. */}
            <iframe
              title="Previzualizare email"
              srcDoc={preview.html}
              sandbox=""
              className="flex-1 w-full min-h-[420px] rounded-b-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
